/**
 * Network activity particles system (white lines)
 * 
 * Handles event-driven white particles with trails for user/AI activity
 * Particles travel along paths from camera to cortex (or vice versa)
 */

import type * as THREE_NS from 'three';
import { state as appState } from '../../../core/state.js';
import { extractNodesFromParticles } from './nodes.js';
import { generateConnectionPath, type PathGenerationOptions } from './connectionPaths.js';
import { loadSpriteTexture } from './spriteLoader.js';

export interface NetworkNode {
  position: { x: number; y: number; z: number };
  id: number;
}

export interface Connection {
  from: NetworkNode;
  to: NetworkNode;
  progress: number; // 0-1: line growth (dies immediately at 1, no shrink phase)
  color: 'white' | 'red';
  duration: number;
  startTime: number;
  pulsePhase: number;
  path: Array<{ x: number; y: number; z: number }>;
  currentSegment: number;
  segmentProgress: number;
  parentGroup: any; // Store parent group for coordinate conversion
  sprite: any; // Sprite at the end of the line to make it look like a trail
  colorHueOffset: number; // Unique hue offset per sprite for variation (limited to blue-green range)
  status?: 'normal' | 'error' | 'warning' | 'cut'; // Connection status for color coding
  isStartup?: boolean; // Flag to indicate this is from startup activation effect
}

export interface NetworkActivityParticlesConfig {
  BASE_MAX_ACTIVE_CONNECTIONS: number;
  LINES_PER_NETWORK_REQUEST: number;
  MAX_ACTIVE_CONNECTIONS_CAP: number;
  CONNECTION_DURATION_MIN: number;
  CONNECTION_DURATION_MAX: number;
  BASE_SPAWN_INTERVAL_MIN: number;
  BASE_SPAWN_INTERVAL_MAX: number;
  ACTIVE_REQUEST_SPAWN_BOOST: number;
}

const DEFAULT_CONFIG: NetworkActivityParticlesConfig = {
  BASE_MAX_ACTIVE_CONNECTIONS: 0, // No lines when no network requests
  LINES_PER_NETWORK_REQUEST: 1, // Lines per active network request - reduced from 3
  MAX_ACTIVE_CONNECTIONS_CAP: 15, // Maximum cap on active connections - reduced from 35
  CONNECTION_DURATION_MIN: 2.0, // Much slower - 2 seconds
  CONNECTION_DURATION_MAX: 4.0, // Much slower - 4 seconds
  BASE_SPAWN_INTERVAL_MIN: 5.0, // Increased from 3.0 - fewer spawns
  BASE_SPAWN_INTERVAL_MAX: 12.0, // Increased from 8.0 - fewer spawns
  ACTIVE_REQUEST_SPAWN_BOOST: 0.3, // Reduced from 0.5 - less boost per active request
};

export interface NetworkActivityParticlesState {
  networkNodes: NetworkNode[];
  networkConnections: Connection[];
  connectionLines: any[];
  lastConnectionSpawn: number;
  nextSpawnTime: number;
}

/**
 * Create network activity particles system
 */
export function createNetworkActivityParticles(
  scene: THREE_NS.Scene,
  camera: THREE_NS.Camera,
  particles: THREE_NS.Points,
  pathOptions: PathGenerationOptions,
  config: NetworkActivityParticlesConfig = DEFAULT_CONFIG
): {
  state: NetworkActivityParticlesState;
  createConnection: (currentTime: number, type?: 'microphone' | 'llm' | 'network', isStartup?: boolean) => void;
  updateConnections: (currentTime: number) => void;
} {
  const { THREE } = pathOptions;
  const {
    BufferGeometry,
    Float32BufferAttribute,
    LineBasicMaterial,
    Line,
    Sprite,
    SpriteMaterial,
  } = THREE as any;

  const networkNodes: NetworkNode[] = [];
  const networkConnections: Connection[] = [];
  const connectionLines: any[] = [];

  // Load sprite texture for beautiful circular sprites
  let spriteTexture: any = null;
  loadSpriteTexture(THREE).then((texture) => {
    spriteTexture = texture;
  });

  // Extract nodes from particles
  const extractedNodes = extractNodesFromParticles(particles);
  networkNodes.push(...extractedNodes);

  let lastConnectionSpawn = 0;
  let nextSpawnTime = 0;
  let startupConnectionIndex = 0; // Counter for ROYGBIV color assignment

  /**
   * Create a new connection
   */
  function createConnection(
    currentTime: number,
    type?: 'microphone' | 'llm' | 'network',
    isStartup?: boolean
  ): void {

    // Get camera position (user's avatar location)
    let cameraPosition = { x: 0, y: 0, z: 50 }; // Default position
    if (camera) {
      cameraPosition = {
        x: (camera as any).position.x,
        y: (camera as any).position.y,
        z: (camera as any).position.z,
      };
    }

    // Color - all lines are white
    const color: 'white' | 'red' = 'white';

    // Duration - consistent for all arcing paths
    const duration =
      config.CONNECTION_DURATION_MIN +
      Math.random() * (config.CONNECTION_DURATION_MAX - config.CONNECTION_DURATION_MIN);

    // Determine connection type
    const connectionType: 'microphone' | 'llm' | 'network' =
      type || 'network';

    // Generate path
    const path = generateConnectionPath(connectionType, pathOptions);

    if (path.length === 0) {
      return;
    }

    // Create dummy nodes for connection structure
    const from = path[0];
    const to = path[path.length - 1];
    const dummyFrom: NetworkNode = { position: from, id: -1 };
    const dummyTo: NetworkNode = { position: to, id: -2 };

    // Special colors for startup: Red, Yellow, Green, Blue (only a few, rest are white)
    const startupColors: { [key: number]: number } = {
      0: 0,    // Red
      1: 0.17, // Yellow
      2: 0.33, // Green
      3: 0.67, // Blue
    };
    let colorHueOffset: number;
    if (isStartup) {
      // Only assign colors to first 4 particles, rest are white (default blue-green)
      if (startupColors.hasOwnProperty(startupConnectionIndex)) {
        colorHueOffset = startupColors[startupConnectionIndex];
      } else {
        // White/default color for the rest
        colorHueOffset = 0.45 + Math.random() * 0.1; // Blue-green range (appears white/cyan)
      }
      startupConnectionIndex++;
    } else {
      // Slight variation in blue-green range (cyan is ~0.5, so vary from 0.45 to 0.55)
      colorHueOffset = 0.45 + Math.random() * 0.1; // Blue-green range only
    }

    const connection: Connection = {
      from: dummyFrom,
      to: dummyTo,
      progress: 0,
      color,
      duration,
      startTime: currentTime,
      pulsePhase: Math.random() * Math.PI * 2,
      path,
      currentSegment: 0,
      segmentProgress: 0,
      parentGroup: null, // Will be set below
      sprite: null,
      colorHueOffset,
      status: 'normal', // Default to normal, can be set to 'error', 'warning', or 'cut'
      isStartup: isStartup || false,
    };

    networkConnections.push(connection);

    // Create Three.js line
    const geometry = new BufferGeometry();
    const positions = new Float32Array(path.length * 3);
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

    const lineColor = 0xffffff; // All lines are white
    const material = new LineBasicMaterial({
      color: lineColor,
      opacity: 10.15, // Start very transparent - will fade to brighter at end
      transparent: true, // Always transparent now
      linewidth: 6, // Much thicker - 6 pixels
    });

    const line = new Line(geometry, material);

    // No pipe groups needed - add directly to scene
    connection.parentGroup = null;
    scene.add(line);
    connectionLines.push(line);

    // Create sprite at the end of the line to make it look like a trail
    // Use beautiful circular sprite texture if available
    // Color will be varied, rotated, and oscillated per sprite
    const spriteMaterial = new SpriteMaterial({
      color: 0x00ffff, // Initial color (will be updated dynamically)
      opacity: 1.0, // Brighter - full opacity
      transparent: true,
      sizeAttenuation: true,
      map: spriteTexture || null, // Use generated sprite texture
      blending: spriteTexture ? (THREE as any).AdditiveBlending : (THREE as any).NormalBlending, // Glowing effect if texture loaded
      depthWrite: false,
    });
    const sprite = new Sprite(spriteMaterial);
    sprite.scale.set(3, 3, 1); // Larger size for visibility - increased from 2
    sprite.position.set(0, 0, 0); // Will be updated each frame
    connection.sprite = sprite;

    scene.add(sprite);

    // Set initial positions (paths are already in world space)
    const initialPositions = geometry.attributes.position.array as Float32Array;
    if (path.length >= 2) {
      // Start with first point
      initialPositions[0] = path[0].x;
      initialPositions[1] = path[0].y;
      initialPositions[2] = path[0].z;
      // Start with second point (line will grow from here)
      initialPositions[3] = path[1].x;
      initialPositions[4] = path[1].y;
      initialPositions[5] = path[1].z;
      geometry.setDrawRange(0, 2);
      geometry.attributes.position.needsUpdate = true;
      geometry.computeBoundingSphere();
    } else if (path.length === 1) {
      // Fallback: if only one point, duplicate it
      initialPositions[0] = path[0].x;
      initialPositions[1] = path[0].y;
      initialPositions[2] = path[0].z;
      initialPositions[3] = path[0].x;
      initialPositions[4] = path[0].y;
      initialPositions[5] = path[0].z;
      geometry.setDrawRange(0, 2);
      geometry.attributes.position.needsUpdate = true;
      geometry.computeBoundingSphere();
    }
  }

  /**
   * Update connections
   */
  function updateConnections(currentTime: number): void {
    // Get active network request count from state to determine how many lines should be active
    const activeNetworkRequests = appState.ui?.activeNetworkRequests || 0;

    // Calculate dynamic max active connections based on active network requests
    const maxActiveConnections = Math.min(
      config.MAX_ACTIVE_CONNECTIONS_CAP,
      config.BASE_MAX_ACTIVE_CONNECTIONS +
        activeNetworkRequests * config.LINES_PER_NETWORK_REQUEST
    );

    // Adjust spawn frequency based on active requests
    const spawnIntervalMultiplier = Math.max(
      0.2,
      1.0 - activeNetworkRequests * config.ACTIVE_REQUEST_SPAWN_BOOST
    );
    const spawnIntervalMin = config.BASE_SPAWN_INTERVAL_MIN * spawnIntervalMultiplier;
    const spawnIntervalMax = config.BASE_SPAWN_INTERVAL_MAX * spawnIntervalMultiplier;

    // Only spawn connections if there are active network requests
    if (activeNetworkRequests > 0) {
      // Spawn new connections if we're below the dynamic max
      if (networkConnections.length < maxActiveConnections) {
        if (currentTime - lastConnectionSpawn >= nextSpawnTime) {
          createConnection(currentTime);
          lastConnectionSpawn = currentTime;
          nextSpawnTime =
            spawnIntervalMin + Math.random() * (spawnIntervalMax - spawnIntervalMin);
        }
      }
    }

    // Update existing connections
    for (let i = networkConnections.length - 1; i >= 0; i--) {
      const conn = networkConnections[i];

      // Calculate elapsed time and progress
      const elapsed = currentTime - conn.startTime;
      conn.progress = Math.min(1, Math.max(0, elapsed / conn.duration));

      // Remove line immediately when it reaches the end (progress >= 1)
      if (conn.progress >= 1) {
        // Remove line
        if (connectionLines[i]) {
          const line = connectionLines[i];
          scene.remove(line);
          line.geometry.dispose();
          (line.material as any).dispose();
        }
        // Remove sprite
        if (conn.sprite) {
          scene.remove(conn.sprite);
          (conn.sprite.material as any).dispose();
        }
        networkConnections.splice(i, 1);
        connectionLines.splice(i, 1);
        continue;
      }

      // Skip if connection hasn't started yet
      if (conn.progress <= 0 || !conn.path || conn.path.length < 2) {
        continue;
      }

      if (connectionLines[i]) {
        const line = connectionLines[i];
        const geometry = line.geometry;
        const positions = geometry.attributes.position.array as Float32Array;

        // Paths are already in world space (no conversion needed)

        // Calculate current end position along the path (in world space)
        const totalPathLength = conn.path.length - 1;
        const pathProgress = conn.progress * totalPathLength;
        const currentSegmentIndex = Math.floor(pathProgress);
        const segmentProgress = pathProgress - currentSegmentIndex;

        const currentSegmentStart = conn.path[Math.min(currentSegmentIndex, conn.path.length - 1)];
        const currentSegmentEnd = conn.path[Math.min(currentSegmentIndex + 1, conn.path.length - 1)];

        let currentEndX =
          currentSegmentStart.x + (currentSegmentEnd.x - currentSegmentStart.x) * segmentProgress;
        let currentEndY =
          currentSegmentStart.y + (currentSegmentEnd.y - currentSegmentStart.y) * segmentProgress;
        let currentEndZ =
          currentSegmentStart.z + (currentSegmentEnd.z - currentSegmentStart.z) * segmentProgress;

        // Extend line by 10% beyond the last path point to ensure it touches the cubes
        if (currentSegmentIndex >= conn.path.length - 2) {
          const lastPoint = conn.path[conn.path.length - 1];
          const secondLastPoint = conn.path[conn.path.length - 2] || lastPoint;
          const directionX = lastPoint.x - secondLastPoint.x;
          const directionY = lastPoint.y - secondLastPoint.y;
          const directionZ = lastPoint.z - secondLastPoint.z;
          const extensionFactor = 0.10; // 10% extension
          currentEndX = lastPoint.x + directionX * extensionFactor;
          currentEndY = lastPoint.y + directionY * extensionFactor;
          currentEndZ = lastPoint.z + directionZ * extensionFactor;
        }

        // Calculate start position (always starts at beginning, no shrink phase)
        const startPathProgress = 0;
        const startSegmentIndex = 0;
        const startSegmentProgress = 0;

        const startSegmentStart = conn.path[Math.min(startSegmentIndex, conn.path.length - 1)];
        const startSegmentEnd = conn.path[Math.min(startSegmentIndex + 1, conn.path.length - 1)];

        const currentStartX =
          startSegmentStart.x + (startSegmentEnd.x - startSegmentStart.x) * startSegmentProgress;
        const currentStartY =
          startSegmentStart.y + (startSegmentEnd.y - startSegmentStart.y) * startSegmentProgress;
        const currentStartZ =
          startSegmentStart.z + (startSegmentEnd.z - startSegmentStart.z) * startSegmentProgress;

        // Build the line from current start to current end
        // Limit line length to avoid "ponytail" effect - only show last N segments
        const maxLineSegments = 3; // Maximum number of segments to show
        const effectiveStartSegment = Math.max(startSegmentIndex, currentSegmentIndex - maxLineSegments);

        let pointIndex = 0;

        // Add start position (or effective start if limited)
        const effectiveStartSegmentStart = conn.path[Math.min(effectiveStartSegment, conn.path.length - 1)];
        const effectiveStartSegmentEnd = conn.path[Math.min(effectiveStartSegment + 1, conn.path.length - 1)];
        const effectiveStartT =
          effectiveStartSegment === startSegmentIndex ? startSegmentProgress : 0;
        const effectiveStartX =
          effectiveStartSegmentStart.x +
          (effectiveStartSegmentEnd.x - effectiveStartSegmentStart.x) * effectiveStartT;
        const effectiveStartY =
          effectiveStartSegmentStart.y +
          (effectiveStartSegmentEnd.y - effectiveStartSegmentStart.y) * effectiveStartT;
        const effectiveStartZ =
          effectiveStartSegmentStart.z +
          (effectiveStartSegmentEnd.z - effectiveStartSegmentStart.z) * effectiveStartT;

        // Add start position (paths are in world space)
        positions[pointIndex * 3] = effectiveStartX;
        positions[pointIndex * 3 + 1] = effectiveStartY;
        positions[pointIndex * 3 + 2] = effectiveStartZ;
        pointIndex++;

        // Add intermediate path points between effective start and end segments (limited)
        for (let seg = effectiveStartSegment + 1; seg < currentSegmentIndex; seg++) {
          if (seg < conn.path.length) {
            positions[pointIndex * 3] = conn.path[seg].x;
            positions[pointIndex * 3 + 1] = conn.path[seg].y;
            positions[pointIndex * 3 + 2] = conn.path[seg].z;
            pointIndex++;
          }
        }

        // Add end position
        positions[pointIndex * 3] = currentEndX;
        positions[pointIndex * 3 + 1] = currentEndY;
        positions[pointIndex * 3 + 2] = currentEndZ;
        pointIndex++;

        // Update geometry - always update if we have at least 2 points
        if (pointIndex >= 2) {
          geometry.setDrawRange(0, pointIndex);
          geometry.attributes.position.needsUpdate = true;
          geometry.computeBoundingSphere();

          // Calculate opacity for tail effect - fade from transparent at start to brighter at end
          // Use progress along the visible line segment to create gradient
          const totalVisibleSegments = pointIndex - 1; // Number of segments in visible line
          if (totalVisibleSegments > 0) {
            // Calculate opacity gradient: transparent at start, brighter at end (where sprite is)
            // Start opacity is very low (0.05), end opacity is higher (0.4)
            const startOpacity = 0.05;
            const endOpacity = 0.4;
            // Average opacity across the line for material
            const avgOpacity = (startOpacity + endOpacity) / 2;
            (line.material as any).opacity = avgOpacity;
          } else {
            (line.material as any).opacity = 0.15; // Fallback
          }

          // Material opacity already set above for tail effect
          // No need for additional pulse - tail effect is enough
          (line.material as any).transparent = true;
          (line.material as any).needsUpdate = true;
        }

        // Update sprite position to track the end of the line
        if (conn.sprite) {
          conn.sprite.position.set(currentEndX, currentEndY, currentEndZ);
          
          // Update sprite color based on status
          let hue: number;
          let saturation: number;
          let lightness: number;
          
          if (conn.status === 'error' || conn.status === 'cut') {
            // Red for errors and cut connections
            hue = 0; // Red
            saturation = 0.9;
            lightness = 0.5;
          } else if (conn.status === 'warning') {
            // Orange/yellow for warnings
            hue = 0.1; // Orange-yellow
            saturation = 0.9;
            lightness = 0.5;
          } else {
            // Blue-green range for normal connections (slight variation per sprite)
            hue = conn.colorHueOffset; // Already in blue-green range (0.45-0.55)
            saturation = 0.8;
            lightness = 0.5;
          }
          
          // Convert HSL to RGB
          const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
          const x = c * (1 - Math.abs((hue * 6) % 2 - 1));
          const m = lightness - c / 2;
          let r = 0, g = 0, b = 0;
          
          if (hue < 1/6) { r = c; g = x; b = 0; }
          else if (hue < 2/6) { r = x; g = c; b = 0; }
          else if (hue < 3/6) { r = 0; g = c; b = x; }
          else if (hue < 4/6) { r = 0; g = x; b = c; }
          else if (hue < 5/6) { r = x; g = 0; b = c; }
          else { r = c; g = 0; b = x; }
          
          const color = Math.floor((r + m) * 255) << 16 | Math.floor((g + m) * 255) << 8 | Math.floor((b + m) * 255);
          (conn.sprite.material as any).color.setHex(color);
        }
      }
    }
  }

  // Initialize spawn timing
  const initTime = performance.now() * 0.001;
  lastConnectionSpawn = initTime;
  nextSpawnTime = config.BASE_SPAWN_INTERVAL_MIN;

  const state: NetworkActivityParticlesState = {
    networkNodes,
    networkConnections,
    connectionLines,
    lastConnectionSpawn,
    nextSpawnTime,
  };

  return {
    state,
    createConnection,
    updateConnections,
  };
}
